import { Request, Response, NextFunction } from "express";
import { AvailabilityService } from "../services/availability.service";
import { Container } from "typedi";
import { jsonStatus, status } from "../../../utils/helpers/api.responses";
import { HttpException } from "../../../utils/exceptions/httpException";
import { pick, removenull } from "../../../utils/helpers/utilities.services";
import { AvailableStatus, AvailableStatusEnum } from "../interfaces/availability.interface";

export class AvailabilityController {
    public availabilityService = Container.get(AvailabilityService);


    public setAvailability = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const availabilityData = pick(req.body, [
                'userId',
                'startDate',
                'endDate',
                'status',
                'jobId',
                'holiday'
            ]) as {
                userId: string;
                startDate: string;
                endDate: string;
                status: AvailableStatus;
                jobId: string;
                holiday?: string;
            };
            removenull(availabilityData);
            const createdBy = req.user._id;
            const t = req.t;

            const availability = await this.availabilityService.setAvailabilityStatus(
                {
                    userId: availabilityData.userId,
                    startDate: new Date(availabilityData.startDate),
                    endDate: new Date(availabilityData.endDate),
                    availabilityStatus: availabilityData.status,
                    jobId: availabilityData.jobId,
                    createdBy,
                    holiday: availabilityData.holiday
                },
                t
            );
            const statusLabel =
                availability.status.toLowerCase() === 'holiday'
                    ? t('Availability.holiday')
                    : t('Availability.unavailability');
            res.status(status.Create).json({
                status: jsonStatus.Create,
                message: t('General.update_success', { field: statusLabel }),
                data: availability
            });
        } catch (error) {
            next(error);
        }
    };



    // public createAvailability = async (req: Request, res: Response, next: NextFunction) => {
    //     try {
    //         const availabilityData = pick(req.body, [
    //             'userId',
    //             'startDate',
    //             'endDate',
    //             'status'
    //         ])
    //         removenull(availabilityData)
    //         const createdBy = new Types.ObjectId(req.user._id);
    //         const t = req.t;

    //         const availabilityDatas = {
    //             ...availabilityData,
    //             createdBy
    //         }
    //         const availability = await this.availabilityService.createAvailability(
    //             availabilityDatas,
    //             t
    //         );
    //         res.status(status.Create).json({
    //             status: jsonStatus.Create,
    //             message: t('General.add_success', { field: t('Availability.availability') }),
    //             data: availability
    //         });
    //     } catch (error) {
    //         next(error);
    //     }
    // };

    public getAvailabilityById = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { id } = req.params;
            const t = req.t;
            const availability = await this.availabilityService.getAvailabilityById(id, t);
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.get_success', { field: t('Availability.unavailability') }),
                data: availability
            });
        } catch (error) {
            next(error);
        }
    };

    public getAllAvailabilities = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const t = req.t;
            const { page = 1, occupation, tickets, limit = 10, startDate, endDate, isPartial = 'false', availability_status, sort = { startDate: 1 }, search } = req.query;
            const filters = req.query.filters ? JSON.parse(req.query.filters as string) : {};

            if (!startDate || !endDate) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'startDate and endDate' }));
            }

            const startDateObj = new Date(startDate as string);
            const endDateObj = new Date(endDate as string);
            if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'startDate or endDate' }));
            }
            if (startDateObj > endDateObj) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'endDate must be after startDate' }));
            }

            const isPartialBool = isPartial.toString().toLowerCase() === 'true';
            if (availability_status) {
                filters.availability_status = availability_status;
            }
            if (occupation) {
                filters.occupation = Array.isArray(occupation) ? occupation : occupation.toString().split(',')
            }
            if (tickets) {
                filters.tickets = Array.isArray(tickets) ? tickets : tickets.toString().split(',');
            }
            const availabilities = await this.availabilityService.getAllAvailabilityStatuses(
                { ...filters, startDate: startDateObj, endDate: endDateObj, isPartial: isPartialBool, search },
                parseInt(page as string),
                parseInt(limit as string),
                sort,
                t
            );

            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.get_success', { field: t('Availability.unavailability') }),
                data: availabilities.data,
                total: availabilities.total,
                page: availabilities.page,
                totalPage: availabilities.totalPage,
            });
        } catch (error) {
            next(error);
        }
    };

    public updateAvailability = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { id } = req.params;
            const updates = req.body;
            updates.updatedBy = req.user._id;
            const t = req.t;
            const updatedAvailability = await this.availabilityService.updateAvailability(id, updates, t);
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.update_success', { field: t('Availability.unavailability') }),
                data: updatedAvailability
            });
        } catch (error) {
            next(error);
        }
    };

    public deleteAvailability = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { ids } = req.body;
            const t = req.t;
            if (!Array.isArray(ids) || ids.length === 0) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'ids[]' }));
            }
            const result = await this.availabilityService.deleteMultipleAvailabilities(ids, req.user._id, t);
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.delete_success', { field: t('Availability.availability') }),
                data: result
            });
        } catch (error) {
            next(error);
        }
    };


    public getCalendarData = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { month, year, date, availableStatus } = req.query;
            const t = req.t;
            if (date && (month || year)) {
                throw new HttpException(status.BadRequest, t('General.invalid_combination', { field: 'date with month or year' }));
            }
            if (!date && (!month || !year)) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'date or month, year' }));
            }
            if (date && !/^\d{4}-\d{2}-\d{2}$/.test(date as string)) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'date' }));
            }
            let normalizedStatus: string | undefined;
            if (availableStatus) {
                let statusValue: string | undefined;
                if (typeof availableStatus === 'string') {
                    statusValue = availableStatus.toLowerCase();
                } else if (Array.isArray(availableStatus) && typeof availableStatus[0] === 'string') {
                    statusValue = availableStatus[0].toLowerCase();
                }
                if (
                    !statusValue ||
                    ![AvailableStatusEnum.AVAILABLE.toLowerCase(), AvailableStatusEnum.UNAVAILABLE.toLowerCase()].includes(statusValue)
                ) {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: 'availableStatus' }));
                }
                normalizedStatus =
                    statusValue === AvailableStatusEnum.AVAILABLE.toLowerCase()
                        ? AvailableStatusEnum.AVAILABLE
                        : AvailableStatusEnum.UNAVAILABLE;
            }
            const { data, availableCount, unavailableCount } = await this.availabilityService.getCalendarData(
                parseInt(month as string),
                parseInt(year as string),
                date as string | undefined,
                normalizedStatus,
                t
            );
            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.get_success', { field: t('Availability.calendar') }),
                available: data.available,
                unavailable: data.unavailable,
                availableCount,
                unavailableCount

            });
        } catch (error) {
            next(error);
        }




    };




    public getUserCalendarData = async (req: Request, res: Response, next: NextFunction) => {
        try {
            const { startDate, endDate } = req.query;
            const t = req.t;
            const userId = req.user._id;

            if (!userId) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'userId' }));
            }

            let normalizedStart: string | undefined;
            let normalizedEnd: string | undefined;

            if (startDate && endDate) {
                if (
                    !/^\d{4}-\d{2}-\d{2}$/.test(startDate as string) ||
                    !/^\d{4}-\d{2}-\d{2}$/.test(endDate as string)
                ) {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: 'startDate or endDate' }));
                }
                normalizedStart = startDate as string;
                normalizedEnd = endDate as string;
            }

            const { data } = await this.availabilityService.getUserCalendarData(
                userId as string,
                normalizedStart,
                normalizedEnd,
                t
            );

            res.status(status.OK).json({
                status: jsonStatus.OK,
                message: t('General.get_success', { field: t('Availability.user_calendar') }),
                data,
            });
        } catch (error) {
            next(error);
        }
    };


    // public removeFromProject = async (req: Request, res: Response, next: NextFunction) => {
    //     try {
    //         const t = req.t
    //         const allocationRequestId = req.params.id
    //         const userId = req.body.userId
    //         const updatedBy = req.user._id
    //         if (!allocationRequestId || !userId) {
    //             throw new HttpException(status.BadRequest, t('General.required', { field: 'allocationRequestId and userId' }));
    //         }
    //         await this.availabilityService.removeLabourFromProject(allocationRequestId, userId, updatedBy, t)
    //         return res.status(status.OK).json({
    //             status: jsonStatus.OK,
    //             message: t('Availability.removeLabour')
    //         })

    //     } catch (error) {
    //         next(error)
    //     }
    // }
}